/**
 * NGO Handout Card Studio — 1/4 page (4.25" × 5.5") double-sided.
 * Front + back preview at 30% scale. Downloads two 1275×1650 PNGs at 300 DPI.
 * Also downloads a single "combined" PNG (both sides side-by-side) for
 * two-sided print orders that expect one file.
 */
import React, { useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Download, RotateCcw, Info, FileDown } from 'lucide-react';
import { HandoutCardFront, HandoutCardBack } from '../components/business-card/HandoutCard';

const PUBLIC_HOST = 'https://americandogclub.com';

const DEFAULTS = (username) => ({
  // FRONT — Set 1 pillars ("Empowering Dog Owners")
  themeLabel: 'Empowering Dog Owners',
  headline: 'Welcome home.',
  subhead: 'Every dog deserves a community.',
  pillar1Title: 'Your Voice',
  pillar1Hint: 'You vote. You decide.',
  pillar2Title: 'Your Data',
  pillar2Hint: 'Records, DNA, ancestry — yours.',
  pillar3Title: 'Your Pack',
  pillar3Hint: 'Real dog friends nearby.',
  pillar4Title: 'Your Perks',
  pillar4Hint: 'Deals that pay you back.',
  ctaLine: 'Scan to join — free',
  // BACK — Set 2 numbered actions
  backHeadline: 'What you can do',
  point1Title: 'Speak up',
  point1Body: 'Members write the rules and elect the officers.',
  point2Title: 'Save up',
  point2Body: 'Member perks that pay for themselves — insurance, gear, vet.',
  point3Title: 'Show up',
  point3Body: 'Local walks, events, and meets with real dog people.',
  point4Title: 'Team up',
  point4Body: 'Health records, DNA, and ancestry — one shared home for your dog.',
  priceFloor: 'Free year courtesy of your adoption — no card required',
  footerNote: 'Scan the QR on the front to claim your free membership year.',
  // NGO co-brand (empty by default; each NGO fills these in)
  ngoName: '',
  ngoSlug: '',
  ngoLogoSrc: '',
  ngoTagline: '',
  ngoAddress: '',
  ngoPhone: '',
  ngoEmail: '',
  ngoWebsite: '',
  // Shared
  website: 'americandogclub.com',
  qrTarget: '',
  xHandle: '',
  tiktokHandle: '',
});

export default function HandoutCard() {
  const { user } = useAuth();
  const [form, setForm] = useState(() => DEFAULTS(user?.username));
  const [exporting, setExporting] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = () => setForm(DEFAULTS(user?.username));

  const points = useMemo(
    () => [
      { title: form.point1Title, body: form.point1Body },
      { title: form.point2Title, body: form.point2Body },
      { title: form.point3Title, body: form.point3Body },
      { title: form.point4Title, body: form.point4Body },
    ],
    [form]
  );

  const pillars = useMemo(
    () => [
      { title: form.pillar1Title, hint: form.pillar1Hint },
      { title: form.pillar2Title, hint: form.pillar2Hint },
      { title: form.pillar3Title, hint: form.pillar3Hint },
      { title: form.pillar4Title, hint: form.pillar4Hint },
    ],
    [form]
  );

  // QR target priority: manual override > NGO slug > public host
  // The /join/[slug] path activates the free-year flow and credits the NGO
  // when it's built out (see ROADMAP: NGO Referral Landing Pages).
  const effectiveQrTarget = useMemo(() => {
    if (form.qrTarget?.trim()) return form.qrTarget.trim();
    if (form.ngoSlug?.trim()) {
      const slug = form.ngoSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
      return `${PUBLIC_HOST}/join/${slug}`;
    }
    return PUBLIC_HOST;
  }, [form.qrTarget, form.ngoSlug]);

  const exportSide = async (ref, filename) => {
    if (!ref.current) return null;
    const el = ref.current;
    const prevT = el.style.transform;
    const prevO = el.style.transformOrigin;
    el.style.transform = 'none';
    el.style.transformOrigin = 'top left';
    try {
      const dataUrl = await toPng(el, {
        pixelRatio: 1,
        cacheBust: true,
        skipFonts: false,
        backgroundColor: '#ffffff',
      });
      const a = document.createElement('a');
      a.download = filename;
      a.href = dataUrl;
      a.click();
      return dataUrl;
    } finally {
      el.style.transform = prevT;
      el.style.transformOrigin = prevO;
    }
  };

  const downloadBoth = async () => {
    setExporting(true);
    try {
      await exportSide(frontRef, 'handout-card-front.png');
      // Small pause between downloads so browsers don't collapse them
      await new Promise((r) => setTimeout(r, 400));
      await exportSide(backRef, 'handout-card-back.png');
      toast.success('Both sides downloaded');
    } catch (err) {
      toast.error('Export failed — try again');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="handout-card-studio">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-medium">NGO Handout Card Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">
          1/4 page portrait (4.25"×5.5"). Preview scaled to 30%. Downloads are print-ready 1275×1650px per side (300 DPI).
        </p>
      </div>

      <div className="grid md:grid-cols-[380px,1fr] gap-6 items-start">
        {/* Edit panel */}
        <Card data-testid="handout-form">
          <CardContent className="p-5 space-y-4">
            <div className="text-sm font-semibold">Front — Empowering theme + 4 pillars (Set 1)</div>
            <div>
              <Label htmlFor="themeLabel" className="text-xs">Theme label</Label>
              <Input id="themeLabel" value={form.themeLabel} onChange={upd('themeLabel')} data-testid="hc-input-theme" />
            </div>
            <div>
              <Label htmlFor="headline" className="text-xs">Headline</Label>
              <Input id="headline" value={form.headline} onChange={upd('headline')} data-testid="hc-input-headline" />
            </div>
            <div>
              <Label htmlFor="subhead" className="text-xs">Sub-headline</Label>
              <Input id="subhead" value={form.subhead} onChange={upd('subhead')} data-testid="hc-input-subhead" />
            </div>
            {[1, 2, 3, 4].map((n) => (
              <div key={`pillar${n}`} className="border rounded-md p-2 space-y-2 bg-amber-50/30">
                <div>
                  <Label htmlFor={`pl${n}t`} className="text-xs">Pillar {n} title</Label>
                  <Input
                    id={`pl${n}t`}
                    value={form[`pillar${n}Title`]}
                    onChange={upd(`pillar${n}Title`)}
                    data-testid={`hc-input-pillar${n}-title`}
                  />
                </div>
                <div>
                  <Label htmlFor={`pl${n}h`} className="text-xs">Pillar {n} hint</Label>
                  <Input
                    id={`pl${n}h`}
                    value={form[`pillar${n}Hint`]}
                    onChange={upd(`pillar${n}Hint`)}
                    data-testid={`hc-input-pillar${n}-hint`}
                  />
                </div>
              </div>
            ))}
            <div>
              <Label htmlFor="ctaLine" className="text-xs">Front CTA</Label>
              <Input id="ctaLine" value={form.ctaLine} onChange={upd('ctaLine')} data-testid="hc-input-cta" />
            </div>

            <div className="text-sm font-semibold border-t pt-3">NGO co-brand (appears on back)</div>
            <div>
              <Label htmlFor="ngoName" className="text-xs">Organization name</Label>
              <Input
                id="ngoName"
                value={form.ngoName}
                onChange={upd('ngoName')}
                placeholder="e.g. Bay Area Dog Rescue"
                data-testid="hc-input-ngo-name"
              />
            </div>
            <div>
              <Label htmlFor="ngoSlug" className="text-xs">NGO slug (activates free-year QR)</Label>
              <Input
                id="ngoSlug"
                value={form.ngoSlug}
                onChange={upd('ngoSlug')}
                placeholder="e.g. bay-area-rescue"
                data-testid="hc-input-ngo-slug"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                QR will point to <code>americandogclub.com/join/{form.ngoSlug || '[slug]'}</code>.
                Landing page grants a free year + credits this NGO. Empty = QR points to the site home.
              </p>
            </div>
            <div>
              <Label htmlFor="ngoLogoSrc" className="text-xs">Logo URL (paste image link)</Label>
              <Input
                id="ngoLogoSrc"
                value={form.ngoLogoSrc}
                onChange={upd('ngoLogoSrc')}
                placeholder="https://ngo.example.org/logo.png"
                data-testid="hc-input-ngo-logo"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Any public image URL (PNG or JPG). File upload coming in a future pass.
              </p>
            </div>
            <div>
              <Label htmlFor="ngoTagline" className="text-xs">Tagline (optional)</Label>
              <Input
                id="ngoTagline"
                value={form.ngoTagline}
                onChange={upd('ngoTagline')}
                placeholder="e.g. Saving one dog at a time"
                data-testid="hc-input-ngo-tagline"
              />
            </div>
            <div>
              <Label htmlFor="ngoAddress" className="text-xs">Address</Label>
              <Input
                id="ngoAddress"
                value={form.ngoAddress}
                onChange={upd('ngoAddress')}
                placeholder="123 Rescue Way, San Francisco, CA 94110"
                data-testid="hc-input-ngo-address"
              />
            </div>
            <div>
              <Label htmlFor="ngoPhone" className="text-xs">Phone</Label>
              <Input
                id="ngoPhone"
                value={form.ngoPhone}
                onChange={upd('ngoPhone')}
                placeholder="(555) 123-4567"
                data-testid="hc-input-ngo-phone"
              />
            </div>
            <div>
              <Label htmlFor="ngoEmail" className="text-xs">Email</Label>
              <Input
                id="ngoEmail"
                value={form.ngoEmail}
                onChange={upd('ngoEmail')}
                placeholder="adopt@bayarearescue.org"
                data-testid="hc-input-ngo-email"
              />
            </div>
            <div>
              <Label htmlFor="ngoWebsite" className="text-xs">Website</Label>
              <Input
                id="ngoWebsite"
                value={form.ngoWebsite}
                onChange={upd('ngoWebsite')}
                placeholder="bayarearescue.org"
                data-testid="hc-input-ngo-website"
              />
            </div>

            <div className="text-sm font-semibold border-t pt-3">Back — 4 numbered actions (Set 2)</div>
            <div>
              <Label htmlFor="backHeadline" className="text-xs">Back headline</Label>
              <Input id="backHeadline" value={form.backHeadline} onChange={upd('backHeadline')} data-testid="hc-input-back-headline" />
            </div>
            {[1, 2, 3, 4].map((n) => (
              <div key={`pt${n}`} className="border rounded-md p-2 space-y-2 bg-amber-50/30">
                <div>
                  <Label htmlFor={`p${n}t`} className="text-xs">Action {n} title</Label>
                  <Input
                    id={`p${n}t`}
                    value={form[`point${n}Title`]}
                    onChange={upd(`point${n}Title`)}
                    data-testid={`hc-input-p${n}-title`}
                  />
                </div>
                <div>
                  <Label htmlFor={`p${n}b`} className="text-xs">Action {n} body</Label>
                  <Textarea
                    id={`p${n}b`}
                    value={form[`point${n}Body`]}
                    onChange={upd(`point${n}Body`)}
                    rows={2}
                    data-testid={`hc-input-p${n}-body`}
                  />
                </div>
              </div>
            ))}

            <div className="text-sm font-semibold border-t pt-3">Pricing + footer</div>
            <div>
              <Label htmlFor="priceFloor">Pricing line</Label>
              <Input id="priceFloor" value={form.priceFloor} onChange={upd('priceFloor')} data-testid="hc-input-price" />
            </div>
            <div>
              <Label htmlFor="footerNote">Footer note</Label>
              <Input id="footerNote" value={form.footerNote} onChange={upd('footerNote')} data-testid="hc-input-footer" />
            </div>

            <div className="text-sm font-semibold border-t pt-3">Shared</div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={form.website} onChange={upd('website')} data-testid="hc-input-website" />
            </div>
            <div>
              <Label htmlFor="qrTarget">QR target override (optional)</Label>
              <Input
                id="qrTarget"
                value={form.qrTarget}
                onChange={upd('qrTarget')}
                placeholder={effectiveQrTarget}
                data-testid="hc-input-qr"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Leave empty to use the NGO slug URL above. Override for testing.
              </p>
            </div>
            <div>
              <Label htmlFor="xHandle">X handle</Label>
              <Input id="xHandle" value={form.xHandle} onChange={upd('xHandle')} data-testid="hc-input-x" />
            </div>
            <div>
              <Label htmlFor="tiktokHandle">TikTok handle</Label>
              <Input id="tiktokHandle" value={form.tiktokHandle} onChange={upd('tiktokHandle')} data-testid="hc-input-tiktok" />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={downloadBoth} disabled={exporting} className="flex-1" data-testid="hc-download-btn">
                <FileDown className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting…' : 'Download both sides'}
              </Button>
              <Button variant="outline" size="icon" onClick={reset} aria-label="Reset" data-testid="hc-reset-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                Paper: heavy text weight (60-70#), not card stock. White background printed CMYK.
                Add 1/8" bleed on upload; the amber top band is safe to bleed off the top edge.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview — Front &amp; Back (30%)</div>
              <div className="flex gap-6 justify-start" data-testid="handout-card-preview">
                <div
                  className="bg-white rounded-lg overflow-hidden border shadow"
                  style={{ width: `${1275 * 0.3}px`, height: `${1650 * 0.3}px` }}
                >
                  <HandoutCardFront
                    ref={frontRef}
                    themeLabel={form.themeLabel}
                    headline={form.headline}
                    subhead={form.subhead}
                    pillars={pillars}
                    ctaLine={form.ctaLine}
                    website={form.website}
                    qrTarget={effectiveQrTarget}
                    ngoName={form.ngoName}
                    ngoLogoSrc={form.ngoLogoSrc}
                  />
                </div>
                <div
                  className="bg-white rounded-lg overflow-hidden border shadow"
                  style={{ width: `${1275 * 0.3}px`, height: `${1650 * 0.3}px` }}
                >
                  <HandoutCardBack
                    ref={backRef}
                    pitchHeadline={form.backHeadline}
                    points={points}
                    priceFloor={form.priceFloor}
                    website={form.website}
                    xHandle={form.xHandle}
                    tiktokHandle={form.tiktokHandle}
                    footerNote={form.footerNote}
                    ngoName={form.ngoName}
                    ngoLogoSrc={form.ngoLogoSrc}
                    ngoTagline={form.ngoTagline}
                    ngoAddress={form.ngoAddress}
                    ngoPhone={form.ngoPhone}
                    ngoEmail={form.ngoEmail}
                    ngoWebsite={form.ngoWebsite}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-amber-50/60 border-amber-200">
            <CardContent className="p-4 text-xs text-amber-900 space-y-1.5">
              <div className="font-semibold">Print tips</div>
              <ul className="list-disc ml-4 space-y-1">
                <li>Order size: 1/4 page portrait, 4.25" × 5.5", double-sided (front + back)</li>
                <li>Weight: 60-70# text (heavier than bond, lighter than card stock)</li>
                <li>Bleed: add 1/8" on all sides in the print shop's uploader; the amber top band is designed to bleed</li>
                <li>Volume: NGOs typically want 250-500 per pickup; Vistaprint / MOO / Overnight Prints all handle this quantity</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
